import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import {
  AppointmentAuthorizationService,
  AuthorizationHistory,
  AuthorizationHistoryEventType,
  AuthorizationHistoryModel,
  AuthorizationModel,
  FacilityService,
  isISODateString,
  PatientAccountModel,
  titlecase,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import _ from 'lodash'
import moment from 'moment-timezone'
import { BehaviorSubject, catchError, concatMap, debounceTime, filter, finalize, map, merge, Observable, of, scan, shareReplay, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs'

/**
 * Rendered in place of a change whose new value is null - an attribute that was
 * cleared rather than set to something. Deliberately a literal rather than a
 * translation key: adding one would require touching every locale file. Swap it
 * for `translateService.instant(...)` if a localised label is wanted.
 */
const CLEARED_VALUE = '\u2014'

@Component({
  selector: 'mmx-auth-history',
  templateUrl: './patient-auth-history-dialog.component.html',
  styleUrls: ['./patient-auth-history-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PatientAuthHistoryDialogComponent implements OnInit, OnDestroy {
  private onDestroy$ = new Subject<void>()

  tz = 'America/New_York'

  readonly calendarFormats = {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'dddd',
    lastDay: '[Yesterday]',
    lastWeek: '[Last] dddd',
    sameElse: 'MM/DD/YYYY',
  }

  inProgress = true

  appointments: Record<string, string> = {}

  public authorizationId$ = new BehaviorSubject<string>(null)
  public accountId$ = new BehaviorSubject<string>(null)

  /** Set when the API reports a further page; drives the "Load more" control. */
  public hasMore = false

  /** Set when a "Load more" request failed, so the events already shown are not mistaken for the whole timeline. */
  public loadMoreFailed = false

  private cursor: string | null = null
  private loadMore$ = new Subject<void>()

  public historiesAndData$ = merge(
    this.authorizationId$.pipe(map((id) => ({ type: 'authorizationId', id }))),
    this.accountId$.pipe(map((id) => (id ? { type: 'accountId', id } : null))),
  )
    .pipe(
      takeUntil(this.onDestroy$),
      filter(Boolean),
      debounceTime(100),
      switchMap((scope) => this.loadPages(scope)),
      shareReplay(1),
    )
  public historiesError$ = this.historiesAndData$.pipe(
    map((resp) => 'error' in resp),
  )

  public histories$: Observable<{ date: moment.Moment, records: AuthorizationHistoryModel[] }[]> = this.historiesAndData$.pipe(
    filter((resp) => 'appointments' in resp && 'histories' in resp),
    map((resp) => {
      for (const appointment of resp.appointments) {
        this.appointments[appointment.id] = moment.tz(appointment.startAt, this.tz).format('LL LT')
      }

      return _(resp.histories)
        .orderBy('createdAt', 'desc')
        .groupBy((authHistory) => {
          return moment.tz(authHistory.createdAt, this.tz).format('YYYY-MM-DD')
        })
        .map((records: AuthorizationHistoryModel[], date) => ({ date: moment.tz(date, this.tz), records }))
        .value()
    }),
    shareReplay(1),
  )

  public hasData$: Observable<boolean> = this.histories$.pipe(
    map((histories) => histories?.length > 0),
    shareReplay(1),
  )

  /**
   * Map of action histories for authorization history records
   * This will hold `ActionHistoryRecord[]` indexed by `AuthorizationHistory.id`
   * These records are loaded on demand when user clicks "Show Error Details" button
   */
  public actionHistoriesMap = {}

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: { authorization?: AuthorizationModel, account?: PatientAccountModel },
    private authApi: AppointmentAuthorizationService,
    private facilityService: FacilityService,
    private translateService: TranslateService,
    private dialogRef: MatDialogRef<PatientAuthHistoryDialogComponent>,
    private cdr: ChangeDetectorRef,
  ) {
    this.tz = this.facilityService.currentTimezone()
  }

  ngOnInit(): void {
    if (this.data.authorization) {
      this.authorizationId$.next(this.data.authorization.id)
    } else if (this.data.account) {
      this.accountId$.next(this.data.account.id)
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  loadMore(): void {
    if (this.hasMore && !this.inProgress) {
      this.loadMore$.next()
    }
  }

  /** Pages accumulate so "Load more" grows the list; a new scope starts a fresh accumulation. */
  private loadPages(scope: { type: string, id: string }) {
    this.cursor = null
    this.hasMore = false
    this.loadMoreFailed = false

    return this.loadMore$.pipe(
      startWith(null),
      tap(() => {
        this.inProgress = true
        this.cdr.markForCheck()
      }),
      concatMap(() => this.authApi.searchHistory({
        [scope.type]: scope.id,
        ...(this.cursor ? { cursor: this.cursor } : {}),
      }).pipe(
        finalize(() => {
          this.inProgress = false
          this.cdr.markForCheck()
        }),
        catchError(() => of({ error: true })),
      )),
      tap((response: any) => {
        // A failed request leaves the cursor and `hasMore` of the last good page alone, so the
        // same page can be asked for again.
        this.loadMoreFailed = 'error' in response && this.hasMore
        if (!('error' in response)) {
          this.cursor = response.cursor ?? null
          this.hasMore = response.hasMore === true
        }
      }),
      scan((accumulated: any, response: any) => {
        if (accumulated == null || 'error' in accumulated) {
          return response
        }

        if ('error' in response) {
          return accumulated
        }

        return {
          ...response,
          histories: [...accumulated.histories ?? [], ...response.histories ?? []],
          appointments: [...accumulated.appointments ?? [], ...response.appointments ?? []],
        }
      }, null),
    )
  }

  getEventTypeText(auth: AuthorizationHistory) {
    switch (auth.eventType) {
      case AuthorizationHistoryEventType.AuthCreated:
        return this.translateService.instant('authorization-history.event-text.created')
      case AuthorizationHistoryEventType.AuthEdited:
        return this.translateService.instant('authorization-history.event-text.updated')
      case AuthorizationHistoryEventType.AuthPulled:
        return this.translateService.instant('authorization-history.event-text.pulled')
      case AuthorizationHistoryEventType.AuthSubmitted:
        return this.translateService.instant('authorization-history.event-text.submitted')
      case AuthorizationHistoryEventType.AuthPullFailed:
        return this.translateService.instant('authorization-history.event-text.pull-failed')
      case AuthorizationHistoryEventType.AuthDeleted:
        return this.translateService.instant('authorization-history.event-text.deleted')
      case AuthorizationHistoryEventType.AuthStatusInitiated:
        return this.translateService.instant('authorization-history.event-text.status-initiated')
      case AuthorizationHistoryEventType.AuthStatusUpdated:
        return this.translateService.instant('authorization-history.event-text.status-updated')
      case AuthorizationHistoryEventType.AuthStatusCanceled:
        return this.translateService.instant('authorization-history.event-text.status-canceled')
      case AuthorizationHistoryEventType.AuthStatusFailed:
        return this.translateService.instant('authorization-history.event-text.status-failed')
      default:
        return ''
    }
  }

  /**
   * Label for the PatientHistory log type, falling back to a title-cased form of the raw value
   * so a type added after these translations still reads as words rather than as `a_b_c`.
   */
  getLogTypeText(auth: AuthorizationHistory): string {
    if (!auth.logType) {
      return ''
    }

    const key = `authorization-history.log-type-values.${auth.logType}`
    const translated = this.translateService.instant(key)

    return translated === key ? titlecase(auth.logType) : translated
  }

  /** Present on PatientHistory rows only; legacy rows never recorded one. */
  shouldShowLogType(auth: AuthorizationHistory): boolean {
    return !!auth.logType
  }

  shouldShowPortal(auth: AuthorizationHistoryModel) {
    const actionsToShowPortal = [
      AuthorizationHistoryEventType.AuthPulled,
      AuthorizationHistoryEventType.AuthPullFailed,
    ]

    return this.getSourceApi(auth) && actionsToShowPortal.includes(auth.eventType)
  }

  getSourceApi(auth: AuthorizationHistoryModel) {
    // The portal recorded on the event itself. Preferred over the authorization's current
    // portal, which is overwritten on every pull and so misreports older events.
    if (auth.portal) {
      return auth.portal
    }

    if (auth.authorization?.sourceApi) {
      return auth.authorization.sourceApi
    }

    if (this.actionHistoriesMap[auth.id]?.length) {
      return this.actionHistoriesMap[auth.id][0].data?.api
    }

    return null
  }

  getByText(auth: AuthorizationHistory) {
    if (!auth.clinician?.name) {
      return ''
    }

    return ' ' + this.translateService.instant('common.by') + ` ${auth.clinician.name}`
  }

  /** Falls back to the legacy `clinician` field for rows written before the actor was structured. */
  getActorName(auth: AuthorizationHistoryModel): string {
    return auth.actor?.clinician?.name ?? auth.clinician?.name ?? ''
  }

  getActorEmail(auth: AuthorizationHistoryModel): string {
    return auth.actor?.clinician?.email ?? auth.clinician?.email ?? ''
  }

  isEmrImport(auth: AuthorizationHistoryModel): boolean {
    return auth.actor?.type === 'emr'
  }

  /**
   * The actor case to render. Collapses everything that is not a person or an EMR import onto
   * `system`, so a row with no actor at all still reads as System rather than as blank.
   */
  getActorKind(auth: AuthorizationHistoryModel): 'clinician' | 'patient' | 'emr' | 'system' {
    const type = auth.actor?.type

    if (type === 'clinician' || type === 'admin') {
      return this.getActorName(auth) ? 'clinician' : 'system'
    }

    if (type === 'patient') {
      return 'patient'
    }

    if (type === 'emr') {
      return 'emr'
    }

    return 'system'
  }

  /**
   * Returns an explicit "not recorded" rather than blank: `trigger` is unset on most historical
   * rows, and a blank cell beside real ones reads as though the first enum value applied.
   */
  getTriggerText(auth: AuthorizationHistoryModel): string {
    if (!auth.trigger) {
      return this.translateService.instant('authorization-history.trigger-values.not-recorded')
    }

    const key = `authorization-history.trigger-values.${auth.trigger}`
    const translated = this.translateService.instant(key)

    // `instant` echoes the key back when there is no entry for it, which would put a raw
    // translation path on screen.
    return translated === key ? auth.trigger : translated
  }

  /**
   * Shown whenever a trigger was recorded. The event list additionally covers rows that predate
   * the field, where "not recorded" is itself the answer rather than an omission.
   */
  shouldShowTrigger(auth: AuthorizationHistoryModel): boolean {
    const triggerableEvents = [
      AuthorizationHistoryEventType.AuthPulled,
      AuthorizationHistoryEventType.AuthPullFailed,
      AuthorizationHistoryEventType.AuthSubmitted,
    ]

    return !!auth.trigger || triggerableEvents.includes(auth.eventType)
  }

  getChanges(auth: AuthorizationHistory): { key: string, value: string }[] {
    if (auth.eventType === AuthorizationHistoryEventType.AuthSubmitted) {
      return auth.submittedData ? [{
        key: this.translateService.instant('authorization-history.changes.submitted'),
        value: ` ${JSON.stringify(auth.submittedData)}`,
      }] : []
    }

    if (!auth.change) {
      return []
    }

    const result = []
    for (let key in auth.change) {
      if (['insuranceId'].includes(key)) {
        continue
      }

      const change = auth.change[key]
      let oldValue = change.oldValue
      let newValue = change.newValue

      if (isISODateString(oldValue)) {
        oldValue = moment.tz(oldValue, this.tz).format('LL LT')
      }
      if (isISODateString(newValue)) {
        newValue = moment.tz(newValue, this.tz).format('LL LT')
      }

      // transform appointment ids into dates
      if (['appointmentIds', 'outdatedAppointmentIds', 'ignoredOutdatedIds'].includes(key)) {
        if (_.isArray(oldValue)) {
          oldValue = oldValue.map(id => this.appointments[id])
        }
        if (_.isArray(newValue)) {
          newValue = newValue.map(id => this.appointments[id])
        }
      }

      // errors is an array of { errorMessage, field? } objects; render the message text
      // instead of letting the generic array-join below stringify each as [object Object]
      if (key === 'errors') {
        if (_.isArray(oldValue)) {
          oldValue = oldValue.map((error: { errorMessage?: string }) => error?.errorMessage ?? error)
        }
        if (_.isArray(newValue)) {
          newValue = newValue.map((error: { errorMessage?: string }) => error?.errorMessage ?? error)
        }
      }

      if (_.isArray(oldValue)) {
        oldValue = oldValue.join(', ')
      }
      if (_.isArray(newValue)) {
        newValue = newValue.join(', ')
      }

      if (oldValue != null && !_.isString(oldValue)) {
        oldValue = oldValue.toString()
      }
      // A cleared attribute arrives as null - AuthDeleted writes
      // `referrerId: { oldValue, newValue: null }` in create-auth-history-record.ts.
      // Without this guard `null.toString()` throws inside change detection and the
      // whole dialog fails to render, not just this row.
      if (newValue == null) {
        newValue = CLEARED_VALUE
      } else if (!_.isString(newValue)) {
        newValue = newValue.toString()
      }

      if (key === 'status') {
        if (oldValue) {
          oldValue = this.translateService.instant(`statuses.authorizations.${oldValue}`)
        }
        if (newValue !== CLEARED_VALUE) {
          newValue = this.translateService.instant(`statuses.authorizations.${newValue}`)
        }
      }

      if (key !== 'attachments') {
        const text = oldValue
          ? this.translateService.instant('authorization-history.changes.changed', {
            from: oldValue,
            to: newValue,
          })
          : this.translateService.instant('authorization-history.changes.set', { to: newValue })

        result.push({
          key,
          value: ` ${text}`,
        })
      } else if (newValue) {
        result.push({
          key: this.translateService.instant('authorization-history.changes.attachments'),
          value: ` <b>${newValue}</b>`,
        })
      }
    }

    return result
  }

  onOpenAuthorization(authorization: AuthorizationModel) {
    this.dialogRef.close(authorization)
  }

  onLoadEventAction(record: AuthorizationHistoryModel) {
    this.authApi.searchHistory({
      authorizationHistoryId: record.id,
      actionHistoryAction: 'authorization.search.failed',
    })
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe((resp) => {
        const actions = resp.actionEvents[record.id]
        this.actionHistoriesMap = {
          ...this.actionHistoriesMap,
          [record.id]: actions?.length ? actions : [{ data: { errors: [{ errorMessage: this.fallbackErrorMessage(record) }] } }],
        }

        this.cdr.markForCheck()
      })
  }

  /**
   * Writers that record failure detail on the event itself rather than through a matching
   * ActionHistory action would otherwise always read as "no details available".
   */
  private fallbackErrorMessage(record: AuthorizationHistoryModel): string {
    return record.errors || this.translateService.instant('authorization-history.no-error-details')
  }
}
